import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUserPosts } from "../services/postService";
import { getUserPublicData } from "../services/userService";
import {
  lookupUsername,
  followUser,
  unfollowUser,
  getFollowers,
  getFollowingList,
  UsernameData,
  Post,
} from "../services/connections";
import { UserPublicData } from "../types/user";
import PostCard from "../components/PostCard";
import { useAuth } from "../contexts/AuthContext";
import { useUserPrivacy } from "../stores";
import Header from "../components/layout/Header";
import BottomNavBar from "../components/layout/BottomNav";
import { trainingTypeService } from "../services/trainingTypeService";
import Loading from "../components/Loading";
import ToastNotification from "../components/ToastNotification";

interface UserWithPublicData {
  userId: string;
  publicData: UserPublicData | null;
}

const Profile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<(Post & { id: string })[]>([]);
  const [userData, setUserData] = useState<UserPublicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Connection state (only show for own profile)
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [foundUser, setFoundUser] = useState<
    (UsernameData & { publicData?: UserPublicData }) | null
  >(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isFoundUserFollowed, setIsFoundUserFollowed] = useState(false);

  // Followers and Following state
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);

  // Dialog states for followers/following
  const [isFollowersDialogOpen, setIsFollowersDialogOpen] = useState(false);
  const [isFollowingDialogOpen, setIsFollowingDialogOpen] = useState(false);
  const [followers, setFollowers] = useState<UserWithPublicData[]>([]);
  const [following, setFollowing] = useState<UserWithPublicData[]>([]);
  const [isLoadingFollowers, setIsLoadingFollowers] = useState(false);
  const [isLoadingFollowing, setIsLoadingFollowing] = useState(false);
  const [unfollowingUserId, setUnfollowingUserId] = useState<string | null>(
    null,
  );
  const [availableSports, setAvailableSports] = useState<
    Record<string, string>
  >({});

  // State for followers follow/unfollow functionality
  const [followingIds, setFollowingIds] = useState<string[]>([]);
  const [followActionLoading, setFollowActionLoading] = useState<string | null>(
    null,
  );

  // Follow/Unfollow state for current profile
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);

  // Share functionality state
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error" | "info">(
    "success",
  );

  // Auto-follow state
  const [isAutoFollowing, setIsAutoFollowing] = useState(false);
  const [autoFollowProcessed, setAutoFollowProcessed] = useState(false);

  const fetchAvailableSports = async () => {
    try {
      const allSports = await trainingTypeService.fetchSports();
      const sportsMap = allSports.reduce(
        (acc: Record<string, string>, sport) => {
          if (sport.id) acc[sport.id] = sport.name;
          return acc;
        },
        {},
      );
      setAvailableSports(sportsMap);
    } catch (error) {
      console.error("Error fetching available sports:", error);
      setAvailableSports({});
    }
  };

  // Resolve a user's sport names for display from their trainingSportIDs
  const getSportNames = (
    publicData?: {
      trainingSportIDs?: string[];
    } | null,
  ): string =>
    (publicData?.trainingSportIDs || [])
      .map((id) => availableSports[id])
      .filter(Boolean)
      .join(", ");

  useEffect(() => {
    fetchAvailableSports();
  }, []);
  // Function to refetch posts after edit/delete
  const refetchPosts = async () => {
    if (!userId) return;

    try {
      const postsResult = await getUserPosts(userId);
      setPosts(postsResult);
    } catch (err) {
      console.error("Error refetching posts:", err);
    }
  };

  const isOwnProfile = currentUser?.uid === userId;

  // Get real-time privacy status from store for current user
  const { isPrivate: storeIsPrivate } = useUserPrivacy(
    isOwnProfile ? currentUser?.uid || null : null,
  );

  // Helper function to get the correct privacy status
  const getPrivacyStatus = useCallback(() => {
    if (isOwnProfile && storeIsPrivate !== null) {
      // For current user, use store value for real-time updates
      return storeIsPrivate;
    }
    // For other users, use userData field
    return userData?.isPrivateProfile || false;
  }, [isOwnProfile, storeIsPrivate, userData?.isPrivateProfile]);

  // Helper function to check if current user can view profile content
  const canViewProfile = () => {
    // Own profile is always viewable
    if (isOwnProfile) return true;

    // Public profiles are always viewable
    if (!getPrivacyStatus()) return true;

    // For private profiles, check if user is following
    // In a full implementation, this would check an approved followers list
    // For now, we'll use the current following status
    return isFollowing;
  };

  // Connection functions (only for own profile)
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setConnectionError("Please enter a username");
      return;
    }

    setIsSearching(true);
    setConnectionError(null);
    setFoundUser(null);
    setSuccessMessage(null);

    try {
      const usernameData = await lookupUsername(searchQuery.trim());

      if (!usernameData) {
        setConnectionError("User not found");
        setIsSearching(false);
        return;
      }

      const publicData = await getUserPublicData(usernameData.userId);

      // Check if the user has a private profile
      if (publicData?.isPrivateProfile === true) {
        setConnectionError(
          "This profile is private and cannot be found in search",
        );
        setIsSearching(false);
        return;
      }

      setFoundUser({
        ...usernameData,
        publicData: publicData || undefined,
      });

      // Check if this user is already being followed
      if (currentUser) {
        const followingList = await getFollowingList(currentUser.uid);
        setIsFoundUserFollowed(followingList.includes(usernameData.userId));
      }
    } catch (err) {
      console.error("Error searching for user:", err);
      setConnectionError("Failed to search for user");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddUser = async () => {
    if (!currentUser || !foundUser) return;

    setIsAdding(true);
    setConnectionError(null);

    try {
      if (isFoundUserFollowed) {
        // Unfollow user
        await unfollowUser(currentUser.uid, foundUser.userId);
        setSuccessMessage(`Successfully unfollowed ${foundUser.username}!`);
        setFollowingCount((prev) => prev - 1);
        setIsFoundUserFollowed(false);
      } else {
        // Follow user
        await followUser(currentUser.uid, foundUser.userId);
        setSuccessMessage(`Successfully followed ${foundUser.username}!`);
        setFollowingCount((prev) => prev + 1);
        setIsFoundUserFollowed(true);
      }

      setTimeout(() => {
        setFoundUser(null);
        setSearchQuery("");
        setSuccessMessage(null);
      }, 2000);
    } catch (err: any) {
      console.error("Error following/unfollowing user:", err);
      setConnectionError(err.message || "Failed to update follow status");
    } finally {
      setIsAdding(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSearchQuery("");
    setFoundUser(null);
    setConnectionError(null);
    setSuccessMessage(null);
    setIsFoundUserFollowed(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSearching) {
      handleSearch();
    }
  };

  const handleViewFollowers = async () => {
    if (!userId) return;

    setIsLoadingFollowers(true);
    setIsFollowersDialogOpen(true);

    try {
      const [followerIds, currentUserFollowing] = await Promise.all([
        getFollowers(userId),
        currentUser ? getFollowingList(currentUser.uid) : Promise.resolve([]),
      ]);

      const followersWithData = await Promise.all(
        followerIds.map(async (uid) => {
          const publicData = await getUserPublicData(uid);
          return { userId: uid, publicData };
        }),
      );

      setFollowers(followersWithData);
      setFollowingIds(currentUserFollowing);
    } catch (error) {
      console.error("Error loading followers:", error);
    } finally {
      setIsLoadingFollowers(false);
    }
  };

  const loadFollowing = async () => {
    if (!userId) return;

    setIsLoadingFollowing(true);
    try {
      const [profileFollowingIds, currentUserFollowing] = await Promise.all([
        getFollowingList(userId),
        currentUser && currentUser.uid !== userId
          ? getFollowingList(currentUser.uid)
          : Promise.resolve(null),
      ]);

      const followingWithData = await Promise.all(
        profileFollowingIds.map(async (uid) => {
          const publicData = await getUserPublicData(uid);
          return { userId: uid, publicData };
        }),
      );

      setFollowing(followingWithData);
      // For other users' profiles, track current user's following list so the
      // follow/unfollow toggles render correctly. For own profile, the list
      // shown IS the current user's following list.
      if (currentUserFollowing !== null) {
        setFollowingIds(currentUserFollowing);
      } else {
        setFollowingIds(profileFollowingIds);
      }
    } catch (error) {
      console.error("Error loading following:", error);
    } finally {
      setIsLoadingFollowing(false);
    }
  };

  const handleViewFollowing = () => {
    setIsFollowingDialogOpen(true);
    loadFollowing();
  };

  const handleUnfollow = async (userIdToUnfollow: string) => {
    if (!currentUser || !isOwnProfile) return;

    setUnfollowingUserId(userIdToUnfollow);

    try {
      await unfollowUser(currentUser.uid, userIdToUnfollow);
      setFollowing((prev) =>
        prev.filter((user) => user.userId !== userIdToUnfollow),
      );
      setFollowingCount((prev) => prev - 1);
    } catch (error) {
      console.error("Error unfollowing user:", error);
    } finally {
      setUnfollowingUserId(null);
    }
  };

  const handleFollowFromFollowers = async (userIdToFollow: string) => {
    if (!currentUser) return;
    if (currentUser.uid === userIdToFollow) return;

    setFollowActionLoading(userIdToFollow);

    try {
      const isCurrentlyFollowing = followingIds.includes(userIdToFollow);

      if (isCurrentlyFollowing) {
        // Unfollow user
        await unfollowUser(currentUser.uid, userIdToFollow);
        setFollowingCount((prev) => prev - 1);
        setFollowingIds((prev) => prev.filter((id) => id !== userIdToFollow));
      } else {
        // Follow user
        await followUser(currentUser.uid, userIdToFollow);
        setFollowingCount((prev) => prev + 1);
        setFollowingIds((prev) => [...prev, userIdToFollow]);
      }
    } catch (error) {
      console.error("Error following/unfollowing user:", error);
    } finally {
      setFollowActionLoading(null);
    }
  };

  // Handle follow/unfollow for current profile
  const handleFollowToggle = async () => {
    if (!currentUser || !userId || isOwnProfile) return;

    setIsFollowLoading(true);
    try {
      if (isFollowing) {
        // Always allow unfollowing
        await unfollowUser(currentUser.uid, userId);
        setIsFollowing(false);
        setFollowerCount((prev) => prev - 1);
      } else {
        // Check if profile is private
        if (getPrivacyStatus()) {
          // Private profiles: Block direct following
          // TODO: Implement follow request system
          console.log(
            "Cannot follow private profile - follow requests not yet implemented",
          );
          alert(
            "This profile is private. Follow requests are not yet implemented.",
          );
          return;
        }

        // Public profiles: Allow direct following
        await followUser(currentUser.uid, userId);
        setIsFollowing(true);
        setFollowerCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error toggling follow:", error);
    } finally {
      setIsFollowLoading(false);
    }
  };

  // Navigate to a user's profile from followers/following dialogs
  const handleNavigateToProfile = (targetUserId: string) => {
    setIsFollowersDialogOpen(false);
    setIsFollowingDialogOpen(false);
    navigate(`/profile/${targetUserId}`);
  };

  // Handle privacy badge click - navigate to settings
  const handlePrivacyBadgeClick = () => {
    if (isOwnProfile) {
      navigate("/settings");
    }
  };

  // Helper function to show toast notifications
  const getProfileSharingUrl = (): string => {
    const baseUrl = window.location.origin;
    const profilePath = `/profile/${userId}?auto-follow=true`;

    console.log("Generated profile URL:", `${baseUrl}${profilePath}`); // For debugging
    return `${baseUrl}${profilePath}`;
  };

  const showToast = (
    message: string,
    type: "success" | "error" | "info" = "success",
  ) => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  // Fallback copy function for HTTP environments (like localhost)
  const fallbackCopyToClipboard = async (text: string): Promise<boolean> => {
    try {
      // Try modern clipboard API first (HTTPS only)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      // Fallback for HTTP environments
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      return successful;
    } catch (error) {
      console.error("All clipboard methods failed:", error);
      return false;
    }
  };

  // Handle profile sharing
  const handleShare = async () => {
    const profileUrl = getProfileSharingUrl();
    const shareText = `Check out @${userData?.username || "this athlete"}'s profile on PEAK^`;

    // Try native share API first (mobile devices)
    if (navigator.share && window.isSecureContext) {
      try {
        await navigator.share({
          title: `${userData?.username || "Athlete"} on PEAK^`,
          text: shareText,
          url: profileUrl,
        });
        setShowShareMenu(false);
        return;
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("Error sharing:", error);
        }
      }
    }

    // Fallback: Copy to clipboard with HTTP support
    const copySuccess = await fallbackCopyToClipboard(profileUrl);
    setShowShareMenu(false);

    if (copySuccess) {
      showToast("Profile link copied to clipboard!");
    } else {
      showToast("Failed to copy link. Please try again.", "error");
    }
  };

  const shareOptions = [
    {
      name: "Copy Link",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
      action: async () => {
        const profileUrl = getProfileSharingUrl();
        const copySuccess = await fallbackCopyToClipboard(profileUrl);
        setShowShareMenu(false);

        if (copySuccess) {
          showToast("Profile sharing link copied!");
        } else {
          showToast("Failed to copy link. Please try again.", "error");
        }
      },
    },
    {
      name: "Share via...",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
          />
        </svg>
      ),
      action: async () => {
        const profileUrl = getProfileSharingUrl();
        const shareText = `Check out @${userData?.username || "this athlete"}'s profile on PEAK^`;

        // Check if Web Share API is supported and we're in a secure context
        if (navigator.share && window.isSecureContext) {
          try {
            await navigator.share({
              title: `${userData?.username || "Athlete"} on PEAK^`,
              text: shareText,
              url: profileUrl,
            });
            setShowShareMenu(false);
          } catch (error) {
            if (error instanceof Error && error.name === "AbortError") {
              // User cancelled the share - that's okay, just close menu
              setShowShareMenu(false);
            } else {
              console.error("Error sharing:", error);
              // Fallback to clipboard copy
              const copySuccess = await fallbackCopyToClipboard(profileUrl);
              setShowShareMenu(false);

              if (copySuccess) {
                showToast("Share cancelled. Link copied to clipboard instead!");
              } else {
                showToast(
                  "Share failed. Please try copying the link instead.",
                  "error",
                );
              }
            }
          }
        } else {
          // Web Share API not supported or not secure context - fallback to copy
          const copySuccess = await fallbackCopyToClipboard(profileUrl);
          setShowShareMenu(false);

          if (copySuccess) {
            showToast("Sharing not supported. Link copied to clipboard!");
          } else {
            showToast(
              "Sharing not available. Please copy link manually.",
              "error",
            );
          }
        }
      },
    },
  ];

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!userId) {
        setError("User ID not found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch user data and posts in parallel
        const [userDataResult, postsResult] = await Promise.all([
          getUserPublicData(userId),
          getUserPosts(userId),
        ]);

        setUserData(userDataResult);

        // ✅ Privacy filter: When viewing others' profiles, only show public posts
        const filteredPosts = isOwnProfile
          ? postsResult // Show all posts on own profile
          : postsResult.filter((post) => post.isPrivate !== true); // Only public posts on others' profiles

        setPosts(filteredPosts);

        // Load connection counts for all profiles
        const [followersList, followingList] = await Promise.all([
          getFollowers(userId),
          getFollowingList(userId),
        ]);
        setFollowerCount(followersList.length);
        setFollowingCount(followingList.length);

        // Check if current user follows this profile (only for other profiles)
        if (currentUser && !isOwnProfile) {
          try {
            const currentUserFollowing = await getFollowingList(
              currentUser.uid,
            );
            setIsFollowing(currentUserFollowing.includes(userId));
          } catch (error) {
            console.error("Error checking follow status:", error);
          }
        }
      } catch (err) {
        console.error("Error fetching profile data:", err);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userId, currentUser, isOwnProfile]);

  // Handle auto-follow functionality
  useEffect(() => {
    const handleAutoFollow = async () => {
      // Check if auto-follow is requested and not already processed
      const urlParams = new URLSearchParams(window.location.search);
      const shouldAutoFollow = urlParams.get("auto-follow") === "true";

      if (
        shouldAutoFollow &&
        !autoFollowProcessed &&
        currentUser &&
        userId &&
        !isOwnProfile &&
        !loading &&
        userData // Ensure user data is loaded
      ) {
        setAutoFollowProcessed(true);

        try {
          // Check if already following
          const currentUserFollowing = await getFollowingList(currentUser.uid);
          const alreadyFollowing = currentUserFollowing.includes(userId);

          if (!alreadyFollowing) {
            setIsAutoFollowing(true);

            // Handle private profiles differently
            if (getPrivacyStatus()) {
              // Private profiles: Block auto-follow
              console.log(
                `Cannot auto-follow private profile @${userData.username} - follow requests not yet implemented`,
              );
              // Don't perform any follow action for private profiles
            } else {
              // Perform the regular auto-follow for public profiles only
              await followUser(currentUser.uid, userId);
              setIsFollowing(true);
              setFollowerCount((prev) => prev + 1);
              console.log(`Auto-followed @${userData.username}`);
            }

            // Clean up the URL by removing the auto-follow parameter
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete("auto-follow");
            window.history.replaceState({}, "", newUrl.toString());
          } else {
            // Already following, just clean up the URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete("auto-follow");
            window.history.replaceState({}, "", newUrl.toString());
          }
        } catch (error) {
          console.error("Error during auto-follow:", error);
        } finally {
          setIsAutoFollowing(false);
        }
      }
    };

    // Only run auto-follow after profile data is loaded
    if (!loading && userData && currentUser) {
      handleAutoFollow();
    }
  }, [
    userId,
    currentUser,
    isOwnProfile,
    loading,
    userData,
    autoFollowProcessed,
  ]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showShareMenu) {
        const target = event.target as Element;
        const shareButton = target.closest('[title="Share profile"]');
        const shareMenu = target.closest(".absolute.top-full");

        if (!shareButton && !shareMenu) {
          setShowShareMenu(false);
        }
      }
    };

    if (showShareMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showShareMenu]);

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Loading size="md" text="Loading profile..." />
        </div>
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen bg-base-200">
        <Header />
        <div className="flex items-center justify-center pt-24">
          <div className="text-center">
            <p className="text-red-500">{error || "Profile not found"}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <Header />

      {/* Auto-follow notification */}
      {isAutoFollowing && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <Loading size="sm" color="white" />
            <span className="text-sm font-medium">
              {`Following @${userData?.username}...`}
            </span>
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto py-6 pt-20">
        {/* Profile Header */}
        <div className="text-center mb-8 relative">
          {/* Share Button - Top Right Corner */}
          <div className="absolute top-0 right-4">
            <div className="relative">
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="p-3 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all duration-200 group"
                title="Share profile"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                  />
                </svg>
              </button>

              {/* Share Menu Dropdown */}
              {showShareMenu && (
                <>
                  {/* Backdrop to close menu */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowShareMenu(false)}
                  ></div>

                  {/* Menu */}
                  <div className="absolute top-full right-0 mt-2 w-48 bg-base-100 rounded-lg shadow-lg border border-base-300 z-20 overflow-hidden">
                    {shareOptions.map((option, index) => (
                      <button
                        key={index}
                        onClick={option.action}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-base-content/80 hover:bg-base-200 transition-colors active:bg-base-200"
                      >
                        <span className="text-base-content/60">
                          {option.icon}
                        </span>
                        <span className="text-sm font-medium">
                          {option.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Avatar */}
          <div className="mb-4">
            {userData.imageUrlNormal || userData.imageUrl ? (
              <img
                src={userData.imageUrlNormal || userData.imageUrl}
                alt={userData.username || "User avatar"}
                className="w-24 h-24 rounded-full object-cover mx-auto ring-4 ring-base-200"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-neutral flex items-center justify-center mx-auto ring-4 ring-base-200">
                <span className="text-white text-2xl font-semibold">
                  {userData.username?.charAt(0).toUpperCase() || "?"}
                </span>
              </div>
            )}
          </div>

          {/* User Info */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-base-content mb-1">
              @{userData.username || "Unknown User"}
            </h1>

            {userData.trainingSportIDs &&
              Object.values(availableSports).length > 0 && (
                <p className="text-base-content/70 mb-2">
                  {userData?.trainingSportIDs
                    ?.map((id: string) => availableSports[id])
                    .filter(Boolean)
                    .join(", ")}{" "}
                  athlete
                </p>
              )}

            {/* Profile Status Badge */}
            {getPrivacyStatus() && (
              <div className="flex justify-center mb-2">
                <button
                  onClick={handlePrivacyBadgeClick}
                  disabled={!isOwnProfile}
                  className={`inline-flex items-center px-2 py-1 rounded-full bg-neutral text-white text-xs font-medium transition-opacity ${
                    isOwnProfile
                      ? "hover:opacity-80 cursor-pointer"
                      : "cursor-default"
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  Private
                </button>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="flex justify-center items-center space-x-16 mb-6 px-4">
            <div className="text-center">
              <div className="text-xl font-bold text-base-content">
                {canViewProfile() ? posts.length : "—"}
              </div>
              <div className="text-sm text-base-content/60">Posts</div>
            </div>

            {/* Followers - clickable for any profile */}
            <button
              onClick={handleViewFollowers}
              className="text-center hover:opacity-80 transition-opacity"
            >
              <div className="text-xl font-bold text-base-content">
                {followerCount}
              </div>
              <div className="text-sm text-base-content/60">Followers</div>
            </button>

            {/* Following - clickable for any profile */}
            <button
              onClick={handleViewFollowing}
              className="text-center hover:opacity-80 transition-opacity"
            >
              <div className="text-xl font-bold text-base-content">
                {followingCount}
              </div>
              <div className="text-sm text-base-content/60">Following</div>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-3">
            {isOwnProfile ? (
              /* Add Connection Button - Only for own profile */
              <button
                onClick={() => setIsDialogOpen(true)}
                className="inline-flex items-center px-6 py-2.5 bg-neutral text-white text-sm font-medium rounded-full hover:bg-neutral/90 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Add Connection
              </button>
            ) : (
              /* Follow/Unfollow Button - For other profiles */
              <button
                onClick={handleFollowToggle}
                disabled={isFollowLoading || isAutoFollowing}
                className={`inline-flex items-center px-8 py-2.5 text-sm font-medium rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isFollowing
                    ? "bg-base-300 text-base-content/80 hover:bg-base-300 border border-base-300"
                    : "bg-neutral text-white hover:bg-neutral/90 shadow-md hover:shadow-lg"
                }`}
              >
                {isFollowLoading || isAutoFollowing ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isFollowing ? "Unfollowing..." : "Following..."}
                  </>
                ) : isFollowing ? (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Following
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    {getPrivacyStatus() ? "Request to Follow" : "Follow"}
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Posts Feed */}
        <div className="space-y-6">
          {!canViewProfile() ? (
            /* Private Profile Message for Non-Followers */
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-base-content/50"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-base-content mb-2">
                This Account is Private
              </h3>
              <p className="text-base-content/70 mb-4 max-w-md mx-auto">
                Follow @{userData.username} to see their posts, training data,
                and connect with them on PEAK^.
              </p>
              <div className="text-sm text-base-content/60">
                Only approved followers can see {userData.username}'s content
              </div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-base-content/50"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
              </div>
              <p className="text-base-content/60 text-lg mb-2">
                {isOwnProfile ? "No posts yet" : "No posts to show"}
              </p>
              <p className="text-base-content/50 text-sm">
                {isOwnProfile
                  ? "Share your first training session to get started"
                  : "This athlete hasn't shared any posts yet"}
              </p>
            </div>
          ) : (
            posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                userId={userId!}
                currentUserId={currentUser?.uid}
                showOwnerActions={isOwnProfile}
                onPostUpdate={refetchPosts}
                onPostDelete={refetchPosts}
              />
            ))
          )}
        </div>
      </div>

      {/* Add User Search Dialog - Only for own profile */}
      {isOwnProfile && isDialogOpen && (
        <div className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-md border border-base-200 max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-base-content">
                Add User
              </h3>
              <button
                onClick={handleCloseDialog}
                className="text-base-content/50 hover:text-base-content/70 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <input
                  id="username-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Search by username"
                  className="w-full px-4 py-2 pr-12 bg-base-100 border border-base-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-base-content focus:border-base-content transition-all"
                  disabled={isSearching}
                />
                <button
                  onClick={handleSearch}
                  disabled={isSearching || !searchQuery.trim()}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-base-content/50 hover:text-base-content/70 disabled:text-base-content/40 disabled:cursor-not-allowed transition-colors"
                >
                  {isSearching ? (
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error Display */}
            {connectionError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{connectionError}</p>
              </div>
            )}

            {/* Success Display */}
            {successMessage && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">{successMessage}</p>
              </div>
            )}

            {/* Found User Display */}
            {foundUser && (
              <div className="mb-4 p-3 bg-base-200 border border-base-300 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {foundUser.publicData?.imageUrlTiny || foundUser.publicData?.imageUrl ? (
                      <img
                        src={foundUser.publicData.imageUrlTiny || foundUser.publicData.imageUrl}
                        alt={foundUser.username}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-neutral rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">
                          {foundUser.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-base-content">
                        @{foundUser.username}
                      </p>
                      <p className="text-sm text-base-content/60">
                        {getSportNames(foundUser.publicData) || "Athlete"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleAddUser}
                    disabled={isAdding}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isFoundUserFollowed
                        ? "bg-red-500/15 text-red-700 hover:bg-red-200"
                        : "bg-blue-500/15 text-blue-700 hover:bg-blue-200"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isAdding ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {isFoundUserFollowed
                          ? "Unfollowing..."
                          : "Following..."}
                      </span>
                    ) : isFoundUserFollowed ? (
                      "Unfollow"
                    ) : (
                      "Follow"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Followers Dialog */}
      {isFollowersDialogOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50">
          {/* Desktop Version */}
          <div className="hidden md:flex items-center justify-center min-h-full p-4">
            <div className="bg-base-100 rounded-2xl shadow-2xl max-w-lg w-full p-8 max-h-[85vh] flex flex-col">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-base-content font-grotesk">
                    Followers
                  </h3>
                  <p className="text-base-content/60 text-sm mt-1">
                    {followerCount}{" "}
                    {isOwnProfile
                      ? "people following you"
                      : `people following @${userData?.username || "user"}`}
                  </p>
                </div>
                <button
                  onClick={() => setIsFollowersDialogOpen(false)}
                  className="p-2 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="overflow-y-auto flex-1 -mx-2 px-2">
                {isLoadingFollowers ? (
                  <div className="flex justify-center items-center py-12">
                    <Loading size="md" text="Loading followers..." />
                  </div>
                ) : followers.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8 text-base-content/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-base-content/70 font-grotesk font-medium">
                      No followers yet
                    </p>
                    <p className="text-sm text-base-content/50 mt-1">
                      Start sharing your workouts to attract followers
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {followers.map((user) => (
                      <div
                        key={user.userId}
                        className="flex items-center gap-3 p-3 border border-base-200 rounded-lg hover:bg-base-200 hover:border-base-300 transition-all group"
                      >
                        <button
                          onClick={() => handleNavigateToProfile(user.userId)}
                          className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                        >
                          {user.publicData?.imageUrlTiny || user.publicData?.imageUrl ? (
                            <img
                              src={user.publicData.imageUrlTiny || user.publicData.imageUrl}
                              alt={user.publicData.username}
                              className="w-10 h-10 rounded-full object-cover ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-neutral flex items-center justify-center ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0">
                              <span className="text-white text-sm font-semibold font-grotesk">
                                {user.publicData?.username
                                  ?.charAt(0)
                                  .toUpperCase() ||
                                  user.userId.slice(-1).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0 text-left">
                            <p className="font-medium text-base-content font-grotesk truncate">
                              {user.publicData?.username ||
                                `User ${user.userId.slice(-4)}`}
                            </p>
                            {getSportNames(user.publicData) && (
                              <p className="text-sm text-base-content/60 truncate">
                                {getSportNames(user.publicData)}
                              </p>
                            )}
                          </div>
                        </button>
                        {currentUser &&
                          currentUser.uid !== user.userId && (
                            <button
                              onClick={() =>
                                handleFollowFromFollowers(user.userId)
                              }
                              disabled={followActionLoading === user.userId}
                              className="px-4 py-2 rounded-lg text-xs font-medium transition-colors bg-base-200 text-base-content/80 hover:bg-base-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {followActionLoading === user.userId
                                ? followingIds.includes(user.userId)
                                  ? "Unfollowing..."
                                  : "Following..."
                                : followingIds.includes(user.userId)
                                  ? "Unfollow"
                                  : "Follow"}
                            </button>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Version */}
          <div className="md:hidden fixed inset-0 bg-base-100 transform transition-all duration-300 ease-out flex flex-col">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-8 h-0.5 bg-base-300 rounded-full"></div>
            </div>

            <div className="flex justify-between items-center px-4 pb-3">
              <div>
                <h3 className="text-lg font-bold text-base-content font-grotesk">
                  Followers
                </h3>
                <p className="text-base-content/60 text-xs mt-0.5">
                  {followerCount}{" "}
                  {isOwnProfile
                    ? "people following you"
                    : `people following @${userData?.username || "user"}`}
                </p>
              </div>
              <button
                onClick={() => setIsFollowersDialogOpen(false)}
                className="p-1.5 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 px-4 pb-6 overflow-y-auto">
              {isLoadingFollowers ? (
                <div className="flex justify-center items-center py-12">
                  <Loading size="md" text="Loading followers..." />
                </div>
              ) : followers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-base-content/50"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-base-content/70 font-grotesk font-medium text-sm">
                    No followers yet
                  </p>
                  <p className="text-xs text-base-content/50 mt-1">
                    Start sharing your workouts to attract followers
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {followers.map((user) => (
                    <div
                      key={user.userId}
                      className="flex items-center gap-3 p-3 border border-base-200 rounded-lg hover:bg-base-200 hover:border-base-300 transition-all group"
                    >
                      <button
                        onClick={() => handleNavigateToProfile(user.userId)}
                        className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                      >
                        {user.publicData?.imageUrlTiny || user.publicData?.imageUrl ? (
                          <img
                            src={user.publicData.imageUrlTiny || user.publicData.imageUrl}
                            alt={user.publicData.username}
                            className="w-8 h-8 rounded-full object-cover ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-neutral flex items-center justify-center ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0">
                            <span className="text-white text-xs font-semibold font-grotesk">
                              {user.publicData?.username
                                ?.charAt(0)
                                .toUpperCase() ||
                                user.userId.slice(-1).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0 text-left">
                          <p className="font-medium text-base-content font-grotesk truncate text-sm">
                            {user.publicData?.username ||
                              `User ${user.userId.slice(-4)}`}
                          </p>
                          {getSportNames(user.publicData) && (
                            <p className="text-xs text-base-content/60 truncate">
                              {getSportNames(user.publicData)}
                            </p>
                          )}
                        </div>
                      </button>
                      {currentUser && currentUser.uid !== user.userId && (
                        <button
                          onClick={() =>
                            handleFollowFromFollowers(user.userId)
                          }
                          disabled={followActionLoading === user.userId}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-base-200 text-base-content/80 hover:bg-base-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {followActionLoading === user.userId
                            ? followingIds.includes(user.userId)
                              ? "Unfollowing..."
                              : "Following..."
                            : followingIds.includes(user.userId)
                              ? "Unfollow"
                              : "Follow"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Following Dialog */}
      {isFollowingDialogOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50">
          {/* Desktop Version */}
          <div className="hidden md:flex items-center justify-center min-h-full p-4">
            <div className="bg-base-100 rounded-2xl shadow-2xl max-w-lg w-full p-8 max-h-[85vh] flex flex-col">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-base-content font-grotesk">
                    Following
                  </h3>
                  <p className="text-base-content/60 text-sm mt-1">
                    {followingCount}{" "}
                    {isOwnProfile
                      ? "people you're following"
                      : `people @${userData?.username || "user"} is following`}
                  </p>
                </div>
                <button
                  onClick={() => setIsFollowingDialogOpen(false)}
                  className="p-2 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="overflow-y-auto flex-1 -mx-2 px-2">
                {isLoadingFollowing ? (
                  <div className="flex justify-center items-center py-12">
                    <Loading size="md" text="Loading following..." />
                  </div>
                ) : following.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8 text-base-content/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-base-content/70 font-grotesk font-medium">
                      Not following anyone yet
                    </p>
                    <p className="text-sm text-base-content/50 mt-1">
                      Find athletes to follow and get inspired
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {following.map((user) => (
                      <div
                        key={user.userId}
                        className="flex items-center gap-3 p-3 border border-base-200 rounded-lg hover:bg-base-200 hover:border-base-300 transition-all group"
                      >
                        <button
                          onClick={() => handleNavigateToProfile(user.userId)}
                          className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                        >
                          {user.publicData?.imageUrlTiny || user.publicData?.imageUrl ? (
                            <img
                              src={user.publicData.imageUrlTiny || user.publicData.imageUrl}
                              alt={user.publicData.username}
                              className="w-10 h-10 rounded-full object-cover ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-neutral flex items-center justify-center ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0">
                              <span className="text-white text-sm font-semibold font-grotesk">
                                {user.publicData?.username
                                  ?.charAt(0)
                                  .toUpperCase() ||
                                  user.userId.slice(-1).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0 text-left">
                            <p className="font-medium text-base-content font-grotesk truncate">
                              {user.publicData?.username ||
                                `User ${user.userId.slice(-4)}`}
                            </p>
                            {getSportNames(user.publicData) && (
                              <p className="text-sm text-base-content/60 truncate">
                                {getSportNames(user.publicData)}
                              </p>
                            )}
                          </div>
                        </button>
                        {isOwnProfile ? (
                          <button
                            onClick={() => handleUnfollow(user.userId)}
                            disabled={unfollowingUserId === user.userId}
                            className="px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {unfollowingUserId === user.userId
                              ? "Unfollowing..."
                              : "Unfollow"}
                          </button>
                        ) : (
                          currentUser &&
                          currentUser.uid !== user.userId && (
                            <button
                              onClick={() =>
                                handleFollowFromFollowers(user.userId)
                              }
                              disabled={followActionLoading === user.userId}
                              className="px-4 py-2 rounded-lg text-xs font-medium transition-colors bg-base-200 text-base-content/80 hover:bg-base-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {followActionLoading === user.userId
                                ? followingIds.includes(user.userId)
                                  ? "Unfollowing..."
                                  : "Following..."
                                : followingIds.includes(user.userId)
                                  ? "Unfollow"
                                  : "Follow"}
                            </button>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Version */}
          <div className="md:hidden fixed inset-0 bg-base-100 transform transition-all duration-300 ease-out flex flex-col">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-8 h-0.5 bg-base-300 rounded-full"></div>
            </div>

            <div className="flex justify-between items-center px-4 pb-3">
              <div>
                <h3 className="text-lg font-bold text-base-content font-grotesk">
                  Following
                </h3>
                <p className="text-base-content/60 text-xs mt-0.5">
                  {followingCount}{" "}
                  {isOwnProfile
                    ? "people you're following"
                    : `people @${userData?.username || "user"} is following`}
                </p>
              </div>
              <button
                onClick={() => setIsFollowingDialogOpen(false)}
                className="p-1.5 text-base-content/50 hover:text-base-content/70 hover:bg-base-200 rounded-full transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 px-4 pb-6 overflow-y-auto">
              {isLoadingFollowing ? (
                <div className="flex justify-center items-center py-12">
                  <Loading size="md" text="Loading following..." />
                </div>
              ) : following.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 bg-base-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-base-content/50"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-base-content/70 font-grotesk font-medium text-sm">
                    Not following anyone yet
                  </p>
                  <p className="text-xs text-base-content/50 mt-1">
                    Find athletes to follow and get inspired
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {following.map((user) => (
                    <div
                      key={user.userId}
                      className="flex items-center gap-3 p-3 border border-base-200 rounded-lg hover:bg-base-200 hover:border-base-300 transition-all group"
                    >
                      <button
                        onClick={() => handleNavigateToProfile(user.userId)}
                        className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                      >
                        {user.publicData?.imageUrlTiny || user.publicData?.imageUrl ? (
                          <img
                            src={user.publicData.imageUrlTiny || user.publicData.imageUrl}
                            alt={user.publicData.username}
                            className="w-8 h-8 rounded-full object-cover ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-neutral flex items-center justify-center ring-2 ring-base-200 group-hover:ring-base-300 transition-all flex-shrink-0">
                            <span className="text-white text-xs font-semibold font-grotesk">
                              {user.publicData?.username
                                ?.charAt(0)
                                .toUpperCase() ||
                                user.userId.slice(-1).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0 text-left">
                          <p className="font-medium text-base-content font-grotesk truncate text-sm">
                            {user.publicData?.username ||
                              `User ${user.userId.slice(-4)}`}
                          </p>
                          {getSportNames(user.publicData) && (
                            <p className="text-xs text-base-content/60 truncate">
                              {getSportNames(user.publicData)}
                            </p>
                          )}
                        </div>
                      </button>
                      {isOwnProfile ? (
                        <button
                          onClick={() => handleUnfollow(user.userId)}
                          disabled={unfollowingUserId === user.userId}
                          className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {unfollowingUserId === user.userId
                            ? "Unfollowing..."
                            : "Unfollow"}
                        </button>
                      ) : (
                        currentUser &&
                        currentUser.uid !== user.userId && (
                          <button
                            onClick={() =>
                              handleFollowFromFollowers(user.userId)
                            }
                            disabled={followActionLoading === user.userId}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-base-200 text-base-content/80 hover:bg-base-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {followActionLoading === user.userId
                              ? followingIds.includes(user.userId)
                                ? "Unfollowing..."
                                : "Following..."
                              : followingIds.includes(user.userId)
                                ? "Unfollow"
                                : "Follow"}
                          </button>
                        )
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNavBar />

      {/* Toast Notification */}
      <ToastNotification
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
        position="bottom"
        duration={3000}
      />
    </div>
  );
};

export default Profile;
